import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useMemo, Fragment } from "react";
import { useTranslation } from "react-i18next";
import MenuIcon from "@mui/icons-material/Menu";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PeopleOutlineOutlinedIcon from "@mui/icons-material/PeopleOutlineOutlined";
import AddShoppingCartOutlinedIcon from "@mui/icons-material/AddShoppingCartOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
// import CampaignIcon from "@mui/icons-material/Campaign";
import { useNavbar } from "hooks/use-navbar";
import { LeftNavbarSection } from "./LeftNavbarSection";
import CustomizedScrollbar from "components/Custom/CustomizedScrollbar";
import packageJson from "../../../../package.json";
// import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import { useStateContext } from "contexts/auth-context";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";

const LeftNavbar = () => {
  const theme = useTheme();
  const navbar = useNavbar();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const {
    state: { authUser, permissions },
  } = useStateContext();

  const sections = useMemo(() => {
    return [
      permissions?.contact?.view && {
        title: t("contact.index"),
        items: [
          {
            title: t("contact.index"),
            path: "/contact",
            icon: <PeopleOutlineOutlinedIcon fontSize="small" />,
            children: [
              permissions?.customer?.view &&
                permissions?.supplier?.view && {
                  title: t("status.all"),
                  path: "/contact/all",
                },
              permissions?.customer?.view && {
                title: t("contact.contact_type.customer"),
                path: "/contact/customer",
              },
              permissions?.supplier?.view && {
                title: t("contact.contact_type.supplier"),
                path: "/contact/supplier",
              },
            ].filter(Boolean),
          },
        ],
      },
      permissions?.inventory?.view && {
        title: t("inventory.index"),
        items: [
          {
            title: t("inventory.index"),
            path: "/inventory",
            icon: <Inventory2OutlinedIcon fontSize="small" />,
            children: [
              permissions?.item?.view && {
                title: t("inventory.item.index"),
                path: "/inventory/item",
              },
              permissions?.goods_receive?.view && {
                title: t("inventory.goods_receive.index"),
                path: "/inventory/goods-receive",
              },
              // {
              //   title: t("inventory.goods_return.index"),
              //   path: "/inventory/goods-return",
              // },
              permissions?.goods_issue?.view && {
                title: t("inventory.goods_issue.index"),
                path: "/inventory/goods-issue",
              },
              permissions?.goods_transfer?.view && {
                title: t("inventory.goods_transfer.index"),
                path: "/inventory/goods-transfer",
              },
              permissions?.goods_adjust?.view && {
                title: t("inventory.goods_adjust.index"),
                path: "/inventory/goods-adjust",
              },
              // {
              //   title: t("inventory.price_list.index"),
              //   path: "/inventory/price-list",
              // },
              permissions?.inventory_export?.view && {
                title: t("inventory.item.report"),
                path: "/inventory/report",
              },
            ].filter(Boolean),
          },
        ],
      },
      permissions?.sales?.view && {
        title: t("sales.index"),
        items: [
          {
            title: t("sales.index"),
            path: "/sales",
            icon: <PeopleAltOutlinedIcon fontSize="small" />,
            children: [
              permissions?.quotation?.view && {
                title: t("sales.quotation.index"),
                path: "/sales/quotation",
              },
              permissions?.billing_note?.view && {
                title: t("sales.billing_note.index"),
                path: "/sales/billing-note",
              },
              // {
              //   title: t("sales.cash_sales.index"),
              //   path: "/sales/cash-sales",
              // },
              // {
              //   title: t("sales.invoice.index"),
              //   path: "/sales/invoice",
              // },
              // {
              //   title: t("sales.return.index"),
              //   path: "/sales/return",
              // },
              // {
              //   title: t("sales.customer_deposit.index"),
              //   path: "/sales/customer-deposit",
              // },
              permissions?.sales_export?.view && {
                title: t("report"),
                path: "/sales/report",
              },
            ]?.filter(Boolean),
          },
        ],
      },
      permissions?.logistic?.view && {
        title: t("logistic.index"),
        items: [
          {
            title: t("logistic.index"),
            path: "/logistic",
            icon: <LocalShippingOutlinedIcon fontSize="small" />,
            children: [
              permissions?.vehicle?.view && {
                title: t("logistic.vehicle.index"),
                path: "/logistic/vehicle",
              },
              permissions?.tire?.view && {
                title: t("logistic.tire.index"),
                path: "/logistic/tire",
              },
              permissions?.delivery_trip?.view && {
                title: t("logistic.delivery_trip.index"),
                path: "/logistic/delivery-trip",
              },
              permissions?.maintenance?.view && {
                title: t("logistic.maintenance.index"),
                path: "/logistic/maintenance",
              },
              permissions?.location?.view && {
                title: t("logistic.location.index"),
                path: "/logistic/location",
              },
              permissions?.logistic_export?.view && {
                title: t("logistic.report.index"),
                path: "/logistic/report",
              },
              permissions?.gantt_chart?.view && {
                title: t("logistic.gantt_chart.index"),
                path: "/logistic/gantt-chart",
              },
            ]?.filter(Boolean),
          },
        ],
      },
      permissions?.purchase?.view && {
        title: t("purchase.index"),
        items: [
          {
            title: t("purchase.index"),
            path: "/purchase",
            icon: <AddShoppingCartOutlinedIcon fontSize="small" />,
            children: [
              // {
              //   title: t("purchase.request.index"),
              //   path: "/purchase/request",
              // },
              permissions?.purchase_order?.view && {
                title: t("purchase.order.index"),
                path: "/purchase/order",
              },
              permissions?.purchase_return?.view && {
                title: t("purchase.return.index"),
                path: "/purchase/return",
              },
              permissions?.purchase_export?.view && {
                title: t("report"),
                path: "/purchase/report",
              },
            ]?.filter(Boolean),
          },
        ],
      },
      {
        title: t("user.index"),
        items: [
          {
            title: t("user.index"),
            path: "/user",
            icon: <PeopleOutlineOutlinedIcon fontSize="small" />,
            children: [
              permissions?.user_account?.view && {
                title: t("user.account.index"),
                path: "/user/account",
              },
              permissions?.role?.view && {
                title: t("user.rbac.index"),
                path: "/user/role",
              },
              {
                title: t("user.setting.index"),
                path: "/user/setting/" + authUser?.id + "?subtab=inventory",
              },
            ].filter(Boolean),
          },
        ],
      },
      permissions?.setting?.view && {
        title: t("setting.index"),
        items: [
          {
            title: t("setting.index"),
            path: "/setting",
            icon: <SettingsOutlinedIcon fontSize="small" />,
            children: [
              // {
              //   title: t("setting.zone.index"),
              //   path: "/setting/zone",
              // },
              permissions?.company?.view && {
                title: t("setting.company.index"),
                path: "/setting/company",
              },
              // {
              //   title: t("setting.approval.index"),
              //   path: "/setting/approval",
              // },
              // {
              //   title: t("setting.contact.index"),
              //   path: "/setting/contact",
              // },
              permissions?.inventory_setting?.view && {
                title: t("setting.inventory.index"),
                path: "/setting/inventory",
              },
              // {
              //   title: t("setting.sales.index"),
              //   path: "/setting/sales",
              // },
              // {
              //   title: t("setting.employee.index"),
              //   path: "/setting/employee",
              // },
            ].filter(Boolean),
          },
        ],
      },
    ].filter(Boolean) as any[];
  }, [authUser?.id, permissions, t]);

  const navItemNavigateHandle = (path: string) => {
    navigate(path);
    if (isSmallScreen) {
      navbar?.sidebar.setIsSidebarOpen(false);
    }
  };

  const content = (
    <CustomizedScrollbar
      sx={{
        height: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100dvh",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Box
            sx={{
              pt: 2,
              px: 3,
            }}
          >
            <Link to={"/landing"}>
              <img
                style={{ cursor: "pointer", height: 90 }}
                src="/static/logo.jpg"
                alt="logo"
              />
            </Link>
            <IconButton
              sx={{
                position: "absolute",
                right: 16,
                height: "auto",
                color: (theme) => theme.palette.primary.main,
                backgroundColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.1),
                "&:hover": {
                  backgroundColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.2),
                },
              }}
              onClick={() => navbar?.sidebar.setIsSidebarOpen(false)}
            >
              <MenuIcon fontSize="small" />
            </IconButton>
          </Box>
          <Divider variant="middle" />
          <Box sx={{ flexGrow: 1 }}>
            {sections.map((section) => (
              <Fragment key={section.title}>
                {/* {section.title === t("user.index") && (
                  <Divider variant="middle" />
                )} */}
                <LeftNavbarSection
                  {...section}
                  isSmallScreen={isSmallScreen}
                  key={section.title}
                  path={pathname}
                  onItemClick={navItemNavigateHandle}
                  sx={{
                    p: 0,
                    "&:first-of-type": {
                      pt: 1,
                      mt: 1,
                    },
                    // "&:nth-of-type(8)": {
                    //   mt: 1,
                    // },
                  }}
                />
                {/* {section.title === t("setting.index") && (
                  <Divider variant="middle" />
                )} */}
              </Fragment>
            ))}
          </Box>
        </Box>
        <Box sx={{ width: "100%", py: 3 }}>
          <Typography align="center" sx={{ opacity: 0.52, fontSize: 14 }}>
            Application version {packageJson.version}
          </Typography>
          <Typography align="center" sx={{ opacity: 0.52, fontSize: 14 }}>
            Powered by NPR Digital Partner
          </Typography>
        </Box>
      </Box>
    </CustomizedScrollbar>
  );
  return (
    <Drawer
      open={navbar?.sidebar.isSidebarOpen}
      onClose={() => navbar?.sidebar.setIsSidebarOpen(false)}
      PaperProps={{
        sx: {
          backgroundColor: "neutral.900",
          borderRightColor: "divider",
          borderRightStyle: "solid",
          borderRightWidth: 1,
          width: 260,
          zIndex: 2,
        },
      }}
      variant={isSmallScreen ? "temporary" : "persistent"}
    >
      {content}
    </Drawer>
  );
};

export default LeftNavbar;
